import { Injectable } from '@angular/core';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import { ApiUrlConstants } from 'src/app/shared/common/ApiUrlConstants';
import { ServicePoTokenAuthService } from 'src/app/shared/services/service-po-token-auth.service';
import { PoTokenAuthService } from 'src/app/shared/services/po-token-auth.service';
// MPS-263 KYC-103 — the declaration template is a PDF, so it is fetched with
// HttpClient directly. WebApiService maps every response through res.data,
// which would discard a Blob.
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface KycLookups {
  businessTypes: { id: number; code: string; displayName: string; displayOrder: number }[];
  bankAccountTypes: { id: number; code: string; displayName: string; displayOrder: number }[];
  documentTypes: { id: number; code: string; displayName: string; isRequired: number; displayOrder: number }[];
  certifications: {
    id: number;
    code: string;
    title: string;
    subtitle: string;
    iconCode: string;
    isMandatory: number;
    applicableSupplierType: string;
    displayOrder: number;
  }[];
}

export interface KycDashboardRow {
  kycId: number;
  kycReferenceNumber: string;
  status: string;
  currentStep: string;
  organizationName: string | null;
  supplierCompanyName: string | null;
  buyerCompanyName?: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  lastClarificationAt: string | null;
  // Admin-side notes surfaced for the row's status pill — clarificationNote
  // is shown when status is CLARIFICATION_REQUIRED, adminFeedback when
  // REJECTED. Both are nullable in every other state.
  clarificationNote?: string | null;
  adminFeedback?: string | null;
  createdAt: string;
  updatedAt: string;
  documentCount: number;
  enabledCertificationCount: number;
}

export interface KycDocument {
  id: number;
  kycId: number;
  documentTypeId: number | null;
  documentTypeCode: string | null;
  documentTypeName: string | null;
  documentName: string | null;
  fileUrl: string;
  fileSizeBytes: number | null;
  fileMimeType: string | null;
  isAdditional: number;
  uploadedAt: string;
}

export interface KycCertification {
  id: number;
  kycId: number;
  certificationId: number;
  certificationCode: string;
  certificationTitle: string;
  certificationSubtitle: string;
  certificationIcon: string;
  displayOrder: number;
  isEnabled: number;
  fileUrl: string | null;
  fileSizeBytes: number | null;
  fileMimeType: string | null;
  certificateNumber: string | null;
  issuer: string | null;
  validityDate: string | null;
}

export interface KycMaster {
  id: number;
  kycReferenceNumber: string;
  supplierId: number;
  companyId: number | null;
  status: string;
  currentStep: string;

  organizationName: string | null;
  businessTypeId: number | null;
  businessTypeCode: string | null;
  businessTypeName: string | null;
  registeredAddress: string | null;
  tradingAddress: string | null;
  logoUrl: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  businessRegistrationNumber: string | null;
  // MPS-263 KYC-101 | Akarsh Singh | 2026-08-29
  countryOfIncorporationId?: number | null;
  taxId: string | null;

  accountHolderName: string | null;
  bankAccountNumberEncrypted: string | null;
  ifscSwiftCode: string | null;
  // MPS-263 KYC-102 — the bank and branch the admin compares the
  // uploaded bank proof against.
  bankName: string | null;
  branchName: string | null;
  bankAccountTypeId: number | null;
  bankAccountTypeCode: string | null;
  bankAccountTypeName: string | null;

  supplierCompanyName: string | null;
  buyerCompanyName?: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
}

export interface KycTimelineEntry {
  id: number;
  kycId: number;
  fromStatus: string | null;
  toStatus: string;
  actorUserId: number | null;
  actorRole: string | null;
  actorName: string | null;
  note: string | null;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class KycService {
  constructor(
    private webApi: WebApiService,
    private servicePoTokenAuth: ServicePoTokenAuthService,
    private poTokenAuth: PoTokenAuthService,
    private http: HttpClient
  ) {}

  /**
   * MPS-263 KYC-103 — download the blank UBO declaration form.
   *
   * `mode` picks the route that matches the caller's session: a supplier in
   * the portal, or one invited by a product or service PO token. The document
   * is identical in all three cases — it carries no supplier data at all.
   */
  downloadUboDeclarationTemplate(
    mode: 'PORTAL' | 'PRODUCT_TOKEN' | 'SERVICE_TOKEN' = 'PORTAL'
  ): Promise<Blob> {
    let url = ApiUrlConstants.kycUboTemplate;
    let options: any = {};
    if (mode === 'PRODUCT_TOKEN') {
      url = ApiUrlConstants.kycUboTemplateByProductPoToken;
      options = this.poTokenAuth.buildAuthHeaders();
    } else if (mode === 'SERVICE_TOKEN') {
      url = ApiUrlConstants.kycUboTemplateByServicePoToken;
      options = this.servicePoTokenAuth.buildAuthHeaders();
    }
    // `responseType: 'blob'` is widened by the generic overload, so the cast
    // goes through unknown rather than pretending the two types overlap.
    return this.http
      .get(environment.apiURL + url, {
        ...options,
        responseType: 'blob' as 'blob',
      })
      .toPromise() as unknown as Promise<Blob>;
  }


  getLookups(): Promise<KycLookups> {
    return this.webApi.getRequest<KycLookups>(ApiUrlConstants.kycGetLookups);
  }

  getDashboard(supplierId: number): Promise<{ rows: KycDashboardRow[] }> {
    return this.webApi.getRequest<{ rows: KycDashboardRow[] }>(
      `${ApiUrlConstants.kycGetDashboard}/${supplierId}`
    );
  }

  getApplication(
    kycId: number,
    supplierId: number
  ): Promise<{ master: KycMaster; documents: KycDocument[]; certifications: KycCertification[] }> {
    return this.webApi.getRequest(
      `${ApiUrlConstants.kycGetApplication}/${kycId}/${supplierId}`
    );
  }

  getTimeline(kycId: number): Promise<{ history: KycTimelineEntry[] }> {
    return this.webApi.getRequest<{ history: KycTimelineEntry[] }>(
      `${ApiUrlConstants.kycGetTimeline}/${kycId}`
    );
  }

  startKyc(payload: { supplierId: number; companyId?: number | null }): Promise<{
    kycId: number;
    kycReferenceNumber: string;
    status: string;
  }> {
    return this.webApi.postRequest(ApiUrlConstants.kycStart, payload);
  }

  // MPS-263 KYC-101 | Akarsh Singh | 2026-08-29
  // Replaces the identifier set for an application. An empty list clears them,
  // which is what a country change does.
  saveIdentifiers(payload: any): Promise<any> {
    return this.webApi.postRequest(ApiUrlConstants.kycSaveIdentifiers, payload);
  }

  saveOrg(payload: any): Promise<any> {
    return this.webApi.postRequest(ApiUrlConstants.kycSaveOrg, payload);
  }

  saveBanking(payload: any): Promise<any> {
    return this.webApi.postRequest(ApiUrlConstants.kycSaveBanking, payload);
  }

  saveDocument(payload: any): Promise<any> {
    return this.webApi.postRequest(ApiUrlConstants.kycSaveDocument, payload);
  }

  saveCertification(payload: any): Promise<any> {
    return this.webApi.postRequest(ApiUrlConstants.kycSaveCertification, payload);
  }

  submit(payload: { kycId: number; statusNote?: string }): Promise<any> {
    return this.webApi.postRequest(ApiUrlConstants.kycSubmit, payload);
  }

  // ============================================================================
  // Supplier KYC routes for product/service PO Token based users (Jira MPS-261)
  // ============================================================================
  fetchSupplierKycDataByProductPoToken(supplierId: number): Promise<{ rows: KycDashboardRow[] }> {
    let apiUrl = `/supplier/kyc/productRfq/fetchSupplierKycDataByPoToken/${supplierId}`
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.getRequestWithOptions<{ rows: KycDashboardRow[] }>(
      apiUrl, options
    );
  }

  fetchSupplierKycDataByServicePoToken(supplierId: number): Promise<{ rows: KycDashboardRow[] }> {
    let apiUrl = `/supplier/kyc/serviceRfq/fetchSupplierKycDataByPoToken/${supplierId}`
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.getRequestWithOptions<{ rows: KycDashboardRow[] }>(
      apiUrl, options
    );
  }

  getLookupsByProductPoToken(): Promise<KycLookups> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.getRequestWithOptions<KycLookups>(ApiUrlConstants.kycGetLookupsByProductPoToken, options);
  }

  getLookupsByServicePoToken(): Promise<KycLookups> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.getRequestWithOptions<KycLookups>(ApiUrlConstants.kycGetLookupsByServicePoToken,options);
  }

  getApplicationByProductPoToken(
    kycId: number,
    supplierId: number
  ): Promise<{ master: KycMaster; documents: KycDocument[]; certifications: KycCertification[] }> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.getRequestWithOptions(
      `${ApiUrlConstants.kycGetApplicationByProductPoToken}/${kycId}/${supplierId}`, options
    );
  }

  getApplicationByServicePoToken(
    kycId: number,
    supplierId: number
  ): Promise<{ master: KycMaster; documents: KycDocument[]; certifications: KycCertification[] }> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.getRequestWithOptions(
      `${ApiUrlConstants.kycGetApplicationByServicePoToken}/${kycId}/${supplierId}`,options
    );
  }

  startKycByProductPoToken(payload: { supplierId: number; companyId?: number | null }): Promise<{
    kycId: number;
    kycReferenceNumber: string;
    status: string;
  }> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycStartByProductPoToken, payload, options);
  }

  startKycByServicePoToken(payload: { supplierId: number; companyId?: number | null }): Promise<{
    kycId: number;
    kycReferenceNumber: string;
    status: string;
  }> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycStartByServicePoToken, payload,options);
  }

  saveOrgByProductPoToken(payload: any): Promise<any> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveOrgByProductPoToken, payload, options);
  }

  saveOrgByServicePoToken(payload: any): Promise<any> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveOrgByServicePoToken, payload,options);
  }

  // MPS-263 KYC-101 — the token equivalents of saveIdentifiers.
  saveIdentifiersByProductPoToken(payload: any): Promise<any> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(
      ApiUrlConstants.kycSaveIdentifiersByProductPoToken, payload, options
    );
  }

  saveIdentifiersByServicePoToken(payload: any): Promise<any> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(
      ApiUrlConstants.kycSaveIdentifiersByServicePoToken, payload, options
    );
  }

  saveBankingByProductPoToken(payload: any): Promise<any> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveBankingByProductPoToken, payload, options);
  }

  saveBankingByServicePoToken(payload: any): Promise<any> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveBankingByServicePoToken, payload,options);
  }

  saveDocumentByProductPoToken(payload: any): Promise<any> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveDocumentByProductPoToken, payload, options);
  }

  saveDocumentByServicePoToken(payload: any): Promise<any> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveDocumentByServicePoToken, payload, options);
  }

  saveCertificationByProductPoToken(payload: any): Promise<any> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveCertificationByProductPoToken, payload, options);
  }

  saveCertificationByServicePoToken(payload: any): Promise<any> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSaveCertificationByServicePoToken, payload, options);
  }

  submitByProductPoToken(payload: { kycId: number; statusNote?: string }): Promise<any> {
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSubmitByProductPoToken, payload, options);
  }

  submitByServicePoToken(payload: { kycId: number; statusNote?: string }): Promise<any> {
    const options = this.servicePoTokenAuth.buildAuthHeaders();
    return this.webApi.postRequest(ApiUrlConstants.kycSubmitByServicePoToken, payload, options);
  }
}
